import { MiniKit } from '@worldcoin/minikit-js';
import { useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/shallow';
import { toaster } from '@/components/toaster';
import { CONSTANTS } from '@/constants';
import { KEY_AUTH, useAuth } from '@/stores/useAuth';
import { getMinikitErrorMessage, mergeAPIError } from '@/utils/errors';
import { request } from '@/utils/request';

const WEEKEND_MILISECOND = 7 * 24 * 60 * 60 * 1000;
const DAY_MILISECOND = 24 * 60 * 60 * 1000;

export function useSignIn() {
  const [setUserInfo, setIsLoading, isLoading] = useAuth(
    useShallow((state) => [
      state.setUserInfo,
      state.setIsLoading,
      state.isLoading,
    ]),
  );

  const signIn = useCallback(async () => {
    try {
      if (!MiniKit.isInstalled()) {
        console.log('Minikit not install');
        return;
      }

      setIsLoading(true);
      const nonce = crypto.randomUUID().replace(/-/g, '');

      const { finalPayload } = await MiniKit.commandsAsync.walletAuth({
        nonce: nonce,
        expirationTime: new Date(new Date().getTime() + WEEKEND_MILISECOND),
        notBefore: new Date(new Date().getTime() - DAY_MILISECOND),
        statement: '',
      });

      if (finalPayload.status !== 'success') {
        if (finalPayload.error_code === 'malformed_request') {
          window.location.reload();
          return;
        }
        if (!finalPayload.details) {
          const msg = getMinikitErrorMessage(finalPayload.error_code);

          throw new Error(msg || 'Sign-in failed');
        }

        throw new Error(finalPayload.details);
      }
      const data = await MiniKit.getUserInfo(finalPayload?.address);
      localStorage.setItem(KEY_AUTH, data.walletAddress);

      setUserInfo({
        address: data.walletAddress,
        profile_picture_url: data.profilePictureUrl ?? CONSTANTS.DEFAULT_AVATAR,
        username: data.username || 'Unknown',
      });

      return {
        address: data.walletAddress,
        profile_picture_url: data.profilePictureUrl ?? CONSTANTS.DEFAULT_AVATAR,
        username: data.username || 'Unknown',
      };
    } catch (error) {
      const message = mergeAPIError(error);
      toaster.error(message);

      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setUserInfo]);

  return {
    signIn,
    isLoading,
  };
}

const QUERY = 'https://usernames.worldcoin.org/api/v1/query';

export async function getUser(address: string) {
  const response = await request(QUERY, {
    method: 'POST',
    body: JSON.stringify({
      addresses: [address],
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const msg = await response
      .json()
      .then((e) => mergeAPIError(e))
      .catch((e) => e.message ?? response.statusText);
    throw new Error(
      `Failed to get user info with status ${response.status} :${msg}`,
    );
  }

  const data = await response.json();
  return data;
}

export function useAutoSignIn() {
  const [setUserInfo, setIsLoading] = useAuth(
    useShallow((state) => [
      state.setUserInfo,
      state.setIsLoading,
      state.isLoading,
    ]),
  );

  useEffect(() => {
    async function fetchUser() {
      try {
        const address = localStorage.getItem(KEY_AUTH);

        if (!address) {
          return;
        }

        setIsLoading(true);

        const data = await MiniKit.getUserInfo(address);
        setIsLoading(false);

        setUserInfo({
          address: data?.walletAddress,
          profile_picture_url:
            data?.profilePictureUrl ?? CONSTANTS.DEFAULT_AVATAR,
          username: data?.username || 'Unknown',
        });
      } catch (error) {
        console.log('Failed to get address', error);
      }
    }

    fetchUser();
  }, [setIsLoading, setUserInfo]);
}
